import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import BusinessIcon from "@mui/icons-material/Business";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleUser, faBars } from "@fortawesome/free-solid-svg-icons";
import {
  Drawer,
  Box,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useLayout } from "../contexts/LayoutContext";
import { useToggles } from "../contexts/FeatureToggleContext";
import { MAIN_MENU, USER_MENU } from "../routes/menuConfig";
import { signOut } from "../utils/authSession";
import { updateUser } from "../store/auth.slice";
import { apiGetAllDomains, apiGetDomainByName } from "../graphQL/domain";
import ProfilePopup from "../components/common/ProfilePopup";
import SettingsPopup from "../components/common/SettingsPopup";
import SidebarNavButton from "../components/common/SidebarNavButton";
import { CompanyKnowledgeDialog } from "../modules/mymagic/panels/VoiceProfileListPanel";
import { layoutTokens } from "../styles/tokens";
import "./Sidebar.scss";

const POPUP_MAP = { profile: "profile", settings: "settings", company: "company" };

// Map sidebar menu item ids -> feature-toggle keys (see config/uiToggles.js).
const MAIN_MENU_TOGGLE_KEYS = {
  ai: "nav.myMagic",
  addressbook: "nav.contacts",
  properties: "nav.properties",
  match: "nav.match",
  campaigns: "nav.campaigns",
  deals: "nav.deals",
  loans: "nav.loans",
  feedback: "nav.feedback",
  files: "nav.files",
  todo: "nav.todo",
  journal: "nav.journal",
  aichat: "nav.aichat",
  analytics: "nav.analytics",
};
const USER_MENU_TOGGLE_KEYS = {
  profile: "user.profile",
  company: "user.company",
  settings: "user.settings",
  logout: "user.logout", // NEVER_TOGGLE -> always resolves ON
};
const ASSETS_BASE = (
  process.env.REACT_APP_PUBLIC_ASSETS_BASE_URL ||
  process.env.REACT_APP_ASSETS_BASE_URL ||
  process.env.REACT_APP_HLS_BASE_URL ||
  "https://assets.yourrighthand.ai"
).replace(/\/+$/, "");

const resolveDomainAvatar = (domain) => {
  const avatar = domain?.avatar;
  if (typeof avatar === "string") return avatar;
  if (avatar && typeof avatar === "object") {
    // Prefer the stable public URL (base/dir/name) over signedUrl, since
    // signed URLs expire and leave the avatar broken.
    const base = ((avatar.base || "").replace(/\/+$/, "")) || ASSETS_BASE;
    const dir = (avatar.dir || "").replace(/^\/+|\/+$/g, "");
    const name = avatar.name || "";
    const domainName = (domain?.name || "").replace(/^\/+|\/+$/g, "");
    if (dir && name) {
      const parts = [base, domainName, dir, name].filter(Boolean);
      const url = parts.join("/");
      const cacheBuster = name.includes(".") ? `?v=${name.split(".")[0]}` : "";
      return `${url}${cacheBuster}`;
    }
    if (avatar.signedUrl) return avatar.signedUrl;
    return "";
  }
  return domain?.company?.imageURL || "";
};

const Sidebar = () => {
  const { isSmall, sidebarOpen, setSidebarOpen, uiSize } = useLayout();
  const { isOn } = useToggles();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const role = String(user?.role || "").toLowerCase();
  // Treat a user as super-admin (can switch domains) ONLY when one of:
  //   - explicit isSuperAdmin flag on the user record
  //   - role contains "super" (superadmin, super_admin, etc.)
  //   - domainList has MORE THAN ONE domain (multi-domain access)
  //   - domainList explicitly includes "all" / "All"
  //
  // A single-domain domainList (e.g., ["kidder"]) means the user is scoped
  // to that one domain — NOT a super-admin.
  const domainList = Array.isArray(user?.domainList) ? user.domainList : [];
  const hasMultiDomainAccess =
    domainList.length > 1 ||
    domainList.some((d) => String(d).toLowerCase() === "all");
  const isSuperAdmin =
    user?.isSuperAdmin === true ||
    role === "superadmin" ||
    role === "super_admin" ||
    role === "sysadmin" ||
    role.includes("super") ||
    hasMultiDomainAccess;
  const userDomain = String(user?.domain || "").trim();
  const [activePopup, setActivePopup] = useState(null);
  const [domains, setDomains] = useState([]);
  const [logoMenuAnchor, setLogoMenuAnchor] = useState(null);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState(null);
  const [mainMenuAnchor, setMainMenuAnchor] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchDomains = () => {
      if (isSuperAdmin) {
        apiGetAllDomains()
          .then((list) => {
            if (cancelled) return;
            setDomains(Array.isArray(list) ? list : []);
          })
          .catch((err) => console.error("Failed to load domains for sidebar:", err));
        return;
      }
      // Single-domain users still need the domain record to render the
      // logo avatar in the sidebar header.
      if (!userDomain) return;
      apiGetDomainByName(userDomain)
        .then((record) => {
          if (cancelled) return;
          setDomains(record ? [record] : []);
        })
        .catch((err) => console.error("Failed to load own domain for sidebar:", err));
    };
    fetchDomains();
    const onChanged = () => fetchDomains();
    window.addEventListener("cs:domainsChanged", onChanged);
    return () => {
      cancelled = true;
      window.removeEventListener("cs:domainsChanged", onChanged);
    };
  }, [isSuperAdmin, userDomain]);

  const selectedDomainObj = useMemo(
    () => domains.find((d) => String(d?.name || "") === userDomain),
    [domains, userDomain]
  );
  const selectedDomainAvatar =
    resolveDomainAvatar(selectedDomainObj);
  const isAllSelected = !userDomain || userDomain === "All";

  const handleMenuClick = (item) => {
    if (item.path) {
      navigate(item.path);
      if (isSmall) setSidebarOpen(false);
    } else if (item.action === "popup") {
      setActivePopup(POPUP_MAP[item.id]);
    } else if (item.action === "logout") {
      signOut();
    }
  };

  const isActive = (item) => item.path && location.pathname.startsWith(item.path);
  const closeLogoMenu = () => setLogoMenuAnchor(null);

  const mainItems = MAIN_MENU.filter((item) => isOn(MAIN_MENU_TOGGLE_KEYS[item.id]));
  const userItems = USER_MENU.filter((item) => isOn(USER_MENU_TOGGLE_KEYS[item.id]));

  // Fit detection: menus stay fully expanded while there is room. When the
  // sidebar is too short (shrunk window or enlarged text), the user section
  // collapses into one Account button first; if even the main menu overflows,
  // it collapses into a hamburger. Both open side flyout menus.
  const sidebarRef = useRef(null);
  const [fit, setFit] = useState({ user: true, main: true });
  useEffect(() => {
    const el = sidebarRef.current;
    if (!el || isSmall) { setFit({ user: true, main: true }); return undefined; }
    const measure = () => {
      const itemH = el.querySelector(".sidebar__item")?.offsetHeight || 56;
      const logoH = el.querySelector(".sidebar__logo")?.offsetHeight || 60;
      const chrome = logoH + 32; // dividers + paddings
      const available = el.clientHeight;
      setFit({
        user: chrome + (mainItems.length + userItems.length) * itemH <= available,
        main: chrome + (mainItems.length + 1) * itemH <= available,
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isSmall, uiSize, mainItems.length, userItems.length]);

  const handleLogoClick = (event) => {
    if (!isSuperAdmin) return;
    setLogoMenuAnchor(event.currentTarget);
  };

  const handleDomainSelect = (domainName) => {
    dispatch(updateUser({ domain: domainName }));
    try { localStorage.setItem("cs_selected_domain", domainName || ""); } catch {}
    closeLogoMenu();
  };

  const menuContent = (
    <Box className="sidebar" role="navigation" ref={sidebarRef}>
      <div className="sidebar__logo">
        <>
          <IconButton
            onClick={handleLogoClick}
            size="small"
            className="sidebar__logo-button"
            aria-label={isSuperAdmin ? "Select domain" : "ContextStory"}
          >
            {isAllSelected ? (
              <img src="/images/logo.png" alt="ContextStory" className="sidebar__logo-img" />
            ) : selectedDomainAvatar ? (
              <Avatar src={selectedDomainAvatar} className="sidebar__logo-avatar" />
            ) : (
              <Avatar className="sidebar__logo-avatar sidebar__logo-avatar--fallback">
                <BusinessIcon sx={{ fontSize: 14 }} />
              </Avatar>
            )}
          </IconButton>
          <Typography
            className="sidebar__logo-label"
            title={isAllSelected ? "All" : userDomain || "Domain"}
          >
            {isAllSelected ? "All" : userDomain || "Domain"}
          </Typography>
          {isSuperAdmin && (
            <Menu
              anchorEl={logoMenuAnchor}
              open={Boolean(logoMenuAnchor)}
              onClose={closeLogoMenu}
              transformOrigin={{ horizontal: "left", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              <MenuItem onClick={() => handleDomainSelect("All")}>
                <ListItemAvatar>
                  <Avatar src="/images/logo.png" sx={{ width: 24, height: 24, borderRadius: "5px", "& img": { objectFit: "cover", borderRadius: "inherit" } }} />
                </ListItemAvatar>
                <ListItemText
                  primary="All"
                  secondary="DOMAIN"
                  primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
                  secondaryTypographyProps={{ fontSize: 11 }}
                />
              </MenuItem>
              {domains.map((domain) => {
                const avatar = resolveDomainAvatar(domain);
                const name = String(domain?.name || "").trim();
                if (!name) return null;
                return (
                  <MenuItem key={domain.id || name} onClick={() => handleDomainSelect(name)}>
                    <ListItemAvatar>
                      {avatar ? (
                        <Avatar src={avatar} sx={{ width: 24, height: 24, borderRadius: "5px", "& img": { objectFit: "cover", borderRadius: "inherit" } }} />
                      ) : (
                        <Avatar sx={{ width: 24, height: 24, bgcolor: "#e2e8f0", color: "#334155", borderRadius: "5px" }}>
                          <BusinessIcon sx={{ fontSize: 16 }} />
                        </Avatar>
                      )}
                    </ListItemAvatar>
                    <ListItemText
                      primary={name}
                      primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
                    />
                  </MenuItem>
                );
              })}
            </Menu>
          )}
        </>
      </div>
      <div className="sidebar__logo-divider" />

      <div className="sidebar__main">
        {isSmall || fit.main ? (
          mainItems.map((item) => (
            <SidebarNavButton
              key={item.id}
              active={isActive(item)}
              icon={item.icon}
              label={item.title}
              comingSoon={item.comingSoon}
              onClick={() => handleMenuClick(item)}
            />
          ))
        ) : (
          <>
            <SidebarNavButton
              icon={faBars}
              label="Menu"
              onClick={(e) => setMainMenuAnchor(e.currentTarget)}
            />
            <Menu
              anchorEl={mainMenuAnchor}
              open={Boolean(mainMenuAnchor)}
              onClose={() => setMainMenuAnchor(null)}
              transformOrigin={{ horizontal: "left", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "top" }}
            >
              {mainItems.map((item) => (
                <MenuItem
                  key={item.id}
                  selected={Boolean(isActive(item))}
                  onClick={() => { setMainMenuAnchor(null); handleMenuClick(item); }}
                  sx={{ gap: 1, fontSize: 13, minWidth: 150 }}
                >
                  <FontAwesomeIcon icon={item.icon} style={{ width: 16 }} />
                  {item.title}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </div>

      <div className="sidebar__divider" />

      <div className="sidebar__user">
        {!isSmall && !fit.user ? (
          <>
            <SidebarNavButton
              icon={faCircleUser}
              label="Account"
              onClick={(e) => setAccountMenuAnchor(e.currentTarget)}
            />
            <Menu
              anchorEl={accountMenuAnchor}
              open={Boolean(accountMenuAnchor)}
              onClose={() => setAccountMenuAnchor(null)}
              transformOrigin={{ horizontal: "left", vertical: "bottom" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              {USER_MENU.filter((item) => isOn(USER_MENU_TOGGLE_KEYS[item.id])).map((item) => (
                <MenuItem
                  key={item.id}
                  onClick={() => { setAccountMenuAnchor(null); handleMenuClick(item); }}
                  sx={{ gap: 1, fontSize: 13, minWidth: 140 }}
                >
                  <FontAwesomeIcon icon={item.icon} style={{ width: 16 }} />
                  {item.title}
                </MenuItem>
              ))}
            </Menu>
          </>
        ) : (
          userItems.map((item) => (
            <SidebarNavButton
              key={item.id}
              icon={item.icon}
              label={item.title}
              onClick={() => handleMenuClick(item)}
            />
          ))
        )}
      </div>

      <ProfilePopup open={activePopup === "profile"} onClose={() => setActivePopup(null)} />
      <SettingsPopup open={activePopup === "settings"} onClose={() => setActivePopup(null)} />
      <CompanyKnowledgeDialog open={activePopup === "company"} onClose={() => setActivePopup(null)} />
    </Box>
  );

  if (isSmall) {
    return (
      <Drawer
        anchor="left"
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        PaperProps={{
          sx: (theme) => ({
            width: layoutTokens.sidebarWidth,
            backgroundColor: theme.palette.custom.sidebar,
            border: "none",
          }),
        }}
      >
        {menuContent}
      </Drawer>
    );
  }

  return menuContent;
};

export default Sidebar;
